<template>
  <button
    class="btn"
    @click="toggle()"
    :aria-label="
      isFavorited(item)
        ? $t('removeFavorite', { name: item.name })
        : $t('addAsFavorite', { name: item.name })
    "
    :aria-pressed="isFavorited(item) ? 'true' : 'false'"
  >
    <WCSvgIcon
      v-if="useSvgIcon"
      name="favourite-icon"
      :class="{ 'wc-favorite-btn--active': isFavorited(item) }"
      icon-size="lg"
    />
    <font-awesome-icon
      v-else
      :class="isFavorited(item) ? 'text-danger' : 'text-muted'"
      :icon="'heart'"
    />
  </button>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ProductDetailMixin from '@/views/ProductDetailPage/mixins/ProductDetailMixin';
import ItemAddToListMixin from '@/modules/lists/mixins/ItemAddToListMixin';
import { defineComponent } from 'vue';
import WCSvgIcon from '@/components/WCSvgIcon/WCSvgIcon.vue';
import useStickyAlerts from '@/modules/alerts/composables/useStickyAlerts';
import StickyAlert from '@/modules/alerts/models/sticky-alert';
import { STICKY_ALERT_TYPE } from '@/modules/alerts/models/sticky-alert-type';
import BannerStickyAlertProps from '@/modules/alerts/models/banner-sticky-alert-props';
import { BANNER_VARIANT } from '@/constants/AppConstants';

export default defineComponent({
  name: 'WCFavoriteButton',
  components: { WCSvgIcon },
  setup() {
    return { stickyAlerts: useStickyAlerts() };
  },
  props: {
    item: {
      type: Object,
    },
    isMobile: {
      type: Boolean,
    },
    useSvgIcon: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      isFavorited: 'lists/isFavorited',
      getItemVariation: 'items/getItemVariation',
      favoritesList: 'lists/getFavoritesList',
    }),
  },
  mixins: [ProductDetailMixin, ItemAddToListMixin],
  methods: {
    ...mapActions({
      removeListItem: 'lists/removeListItem',
      createVariation: 'items/createVariation',
    }),
    /**
     * Get variation from the favorites list by mapping with the item id
     * If there are more than 1 variation for a favorited item, redirect to list page
     * Else return the variation
     */
    getVariation(item) {
      const itemsCount = this.favoritesList?.items.filter(i => i.id === item.id).length;
      if (itemsCount > 1) {
        this.$router.push(`/me/list/${this.favoritesList.id}`);
      } else {
        return this.favoritesList?.items.find(i => i.id === item.id).variation;
      }
      return null;
    },
    showBanner(variant, message, timeoutMs) {
      this.stickyAlerts.addAlert(
        new StickyAlert(
          STICKY_ALERT_TYPE.BANNER,
          new BannerStickyAlertProps(variant, message, { timeoutMs }),
        ),
      );
    },
    async add() {
      try {
        await this.addItemToList({
          list: { name: 'Favorites', type: 'FAVORITES' },
          item: this.item,
          variation: this.getItemVariation || null,
        });
        this.showBanner(BANNER_VARIANT.SUCCESS, 'Item added to Favorites list', 2000);
      } catch (error) {
        this.showBanner(BANNER_VARIANT.DANGER, error.message, 7000);
      }
    },
    async remove() {
      try {
        await this.removeListItem({
          list: { name: 'Favorites', type: 'FAVORITES' },
          item: this.item,
          variation: this.getVariation(this.item) || null,
        });
      } catch (error) {
        this.showBanner(BANNER_VARIANT.DANGER, error.message, 7000);
      }
    },
    async toggle() {
      // non MTO item's favorite workflow
      if (!(this.item.hasModifiers || this.item.hasLinkedItems)) {
        if (this.isFavorited(this.item)) this.remove();
        else this.add();
      }
      // MTO item's favorite workflow
      else if (this.item.hasModifiers || this.item.hasLinkedItems) {
        if (this.isFavorited(this.item)) this.remove();
        // customizeItemToAddToList accepts two arguments -isMobile and fromMTOItemFavIcon
        else this.customizeItemToAddToList(this.isMobile, true);
      }
    },
  },
});
</script>

<style scoped>
.wc-favorite-btn--active :deep(svg path) {
  fill: var(--danger-darker-2);
}
</style>
